import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { TokenHandlerService } from '@celerofinancas/ui-oauth';

/**
 * Blocks an authenticated route when there is no session.
 *
 * Without it, opening `/dashboard` straight from the address bar (or in a private
 * window, or after the token expires) renders the whole screen with no token:
 * `bfm-dashboard-finance` calls the API, takes a `401` on `GET /v2/me/read`, and
 * the user stares at `R$ 0,00` in every block with an error in the console. It
 * looks like zeroed data, not a missing session — a poor way to find out you need
 * to sign in.
 *
 * It also accepts a valid refresh token with an expired access token, instead of
 * sending the user to login: `TokenRefreshInterceptor` is in the interceptor chain
 * (see `interceptors.ts`) and renews on the first request. The mock's access token
 * lasts 30 minutes and the refresh 8 hours, so coming back to the tab after a
 * coffee should not cost a new login.
 *
 * It stores the requested route in `returnUrl` so login returns the user where
 * they wanted to be, not always to the dashboard.
 *
 * @param _route activated route snapshot, unused — the decision only depends on the session
 * @param state router state, read for the URL that goes into `returnUrl`
 * @returns `true` when there is a usable session, or a redirect to `/login` otherwise
 */
export const authGuard: CanActivateFn = (_route, state) => {
  const tokenHandler = inject(TokenHandlerService);
  const router = inject(Router);

  if (tokenHandler.isAccessTokenValid || tokenHandler.isRefreshTokenValid) {
    return true;
  }

  return router.createUrlTree(['/login'], { queryParams: { returnUrl: state.url } });
};
